import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {
  palette,
  spacing,
  radius,
  shadow,
  transition,
  typography,
  gradient,
  easing,
  duration,
  zIndex,
  layout,
  status,
} from '../../../../style-utils/theme';
import {
  fadeInUp,
  scaleIn,
  stagger,
} from '../../../../style-utils/motion';
import { up, down } from '../../../../style-utils/media-queries';
import { InlineMessage, Pop, m } from '../../../../motion';

/* ── Shell ─────────────────────────────────────────────────────────────── */

/* No entrance of its own: the dashboard's ContentTransition brings the screen
   in, and a second fade on top of it read as the page loading twice. */
export const DashboardContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-height: 0;
  display: flex;
  flex: 1 0 auto;
  flex-direction: column;
  background: transparent;
`;

export const PortfoliosListing = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  margin-bottom: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};

  ${up('md')} {
    flex-direction: row;
    align-items: center;
    padding: ${spacing.sm} ${spacing.md};

    /* first-CHILD, not first-of-type: with a div title and a button action,
       BOTH were ":first-of-type" of their own tag, so the button also carried
       margin-right: auto and sat glued to the title instead of on the right
       edge. Only the title pushes; the action stays flush right with the row's
       padding as its gap. */
    & > *:first-child {
      margin-right: auto;
    }
  }

  ${down('md')} {
    & > * {
      width: 100%;
    }
  }
`;

export const Title = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const NewPortfolioButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  color: ${palette.white};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  cursor: pointer;
  box-shadow: ${shadow.brand};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${up('lg')} {
    height: ${layout.controlHeight};
  }

  &:hover {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }

  &:active {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.brand}, ${shadow.focus};
  }
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
`;

/* ── Table ─────────────────────────────────────────────────────────────── */

export const StyledFlexRow = styled.div<{
  index?: number;
  isHeaderRow?: boolean;
  isMenuOpen?: boolean;
}>`
  display: flex;
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
  margin-bottom: ${spacing.xs};
  position: relative;

  /* The entrance animation below ends on transform: translateY(0) and is held
   * by fill-mode "both", so every row keeps a transform and therefore its own
   * stacking context. A popover inside row N would be painted under row N+1 no
   * matter how high its z-index goes — the fix has to be applied to the row
   * itself, which is why the open menu lifts the whole row. */
  ${({ isMenuOpen }) =>
    isMenuOpen &&
    css`
      z-index: ${zIndex.dropdown};
    `}

  ${({ isHeaderRow, index = 0 }) =>
    isHeaderRow
      ? css`
          margin-bottom: ${spacing.xs};
        `
      : css`
          animation: ${fadeInUp} ${duration.base} ${easing.entrance} both;
          animation-delay: ${stagger(index)};
        `}
`;

/**
 * The row surface. Unlike the buildings table this is a column, because an
 * expanded portfolio renders its buildings inside the same card — the lift on
 * hover is suppressed while open so the panel doesn't wobble under the cursor.
 */
export const RowCard = styled.div<{ isHead?: boolean; isExpanded?: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  /* NOT overflow:hidden — the row-actions popover is absolutely positioned
   * inside this card and would be clipped away by it. The expansion panel
   * rounds its own bottom corners instead. */
  overflow: visible;
  transition: ${transition.colors},
    transform ${duration.base} ${easing.standard};

  &:hover {
    background: ${palette.irisTintSoft};
    transform: translateY(-1px);
    box-shadow: ${shadow.sm};
  }

  ${({ isExpanded }) =>
    isExpanded &&
    css`
      background: ${palette.surface};
      border-color: ${palette.lineStrong};
      box-shadow: ${shadow.sm};

      &:hover {
        background: ${palette.surface};
        transform: none;
        box-shadow: ${shadow.sm};
      }
    `}

  ${({ isHead }) =>
    isHead &&
    css`
      background: transparent;
      border: none;
      border-bottom: 1px solid ${palette.line};
      border-radius: 0;
      box-shadow: none;
      overflow: visible;

      &:hover {
        background: transparent;
        transform: none;
        box-shadow: none;
      }

      & p {
        text-transform: uppercase;
        letter-spacing: ${typography.tracking.caps};
        font-weight: ${typography.weight.semibold};
        color: ${palette.slate};
      }

      ${down('md')} {
        display: none;
      }
    `}
`;

export const RowCells = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;

  ${down('md')} {
    flex-direction: column;
    align-items: stretch;
  }
`;

/* Column widths must total 100. */
export const ItemContainer = styled.div<{
  width?: number;
  isHeader?: boolean;
  isActions?: boolean;
}>`
  box-sizing: border-box;
  width: ${({ width }) => (width ? `${width}%` : 'auto')};
  min-width: 0;
  min-height: ${({ isHeader }) => (isHeader ? '48px' : '64px')};
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  overflow: hidden;
  background: transparent;
  position: ${({ isActions }) => (isActions ? 'relative' : 'static')};

  ${({ isActions }) =>
    isActions &&
    css`
      justify-content: flex-end;
      overflow: visible;
    `}

  & p {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100%;
    min-width: 0;
  }

  ${down('md')} {
    width: 100% !important;
    min-height: 0;
    justify-content: space-between;
    gap: ${spacing.sm};
    padding: ${spacing.sm};
    border-bottom: 1px solid ${palette.line};

    &::before {
      content: attr(data-label);
      flex: 0 0 auto;
      color: ${palette.slate};
      font-family: ${typography.ui};
      font-size: ${typography.size.xxs}px;
      font-weight: ${typography.weight.semibold};
      letter-spacing: ${typography.tracking.caps};
      text-transform: uppercase;
    }

    & p {
      white-space: normal;
      text-align: right;
    }

    ${({ isActions }) =>
      isActions &&
      css`
        justify-content: flex-end;
        border-bottom: none;

        &::before {
          display: none;
        }
      `}
  }
`;

/**
 * The name cell owns the expander at every width — one control rather than a
 * desktop column plus a phone duplicate, so there is a single thing to focus,
 * label and tab to.
 */
export const NameCell = styled(ItemContainer)`
  gap: ${spacing.xs};
  padding-left: ${spacing.xs};

  ${down('md')} {
    justify-content: flex-start;
    padding-left: ${spacing.xs};

    &::before {
      display: none;
    }

    & p {
      text-align: left;
      font-weight: ${typography.weight.semibold};
    }
  }
`;

export const NameStack = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  flex-wrap: wrap;
`;

export const ExpandButton = styled.button<{ isExpanded?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${palette.slate};
  cursor: pointer;
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  & svg {
    transition: transform ${duration.base} ${easing.standard};
    transform: rotate(${({ isExpanded }) => (isExpanded ? '180deg' : '0deg')});
  }

  &:hover {
    background: ${palette.irisTint};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${down('md')} {
    /* Keep the tap target honest once the row becomes a card. */
    width: ${layout.touchTarget};
    height: ${layout.touchTarget};
    margin-left: -${spacing.xs};
  }
`;

/* ── Pills ─────────────────────────────────────────────────────────────── */

export const Pill = styled.span<{ tone?: 'brand' | 'neutral' | 'success' }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  flex: 0 0 auto;
  height: 24px;
  padding: 0 ${spacing.xs};
  border-radius: ${radius.pill};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  background: ${({ tone = 'neutral' }) => status[tone].bg};
  color: ${({ tone = 'neutral' }) => status[tone].fg};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: ${radius.circle};
    background: ${({ tone = 'neutral' }) => status[tone].dot};
  }
`;

export const CountPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 28px;
  height: 24px;
  padding: 0 ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  color: ${palette.inkSoft};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
`;

/* ── Row actions ───────────────────────────────────────────────────────── */

export const ActionTrigger = styled.button`
  -webkit-appearance: none;
  appearance: none;
  width: ${layout.touchTarget};
  height: ${layout.touchTarget};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  border-radius: ${radius.xs};
  background: transparent;
  color: ${palette.slate};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTint};
    color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${down('md')} {
    width: ${layout.touchTarget};
    height: ${layout.touchTarget};
  }
`;

/* Deliberately no full-screen click-catcher div here: `position: fixed` resolves
 * against the nearest transformed ancestor, and the animated row IS one, so such
 * an overlay would only cover its own row. The screen closes the menu with a
 * document-level listener instead (see dashboard-portfolios.tsx). */
/* Stacks rather than sits in a row: the menu carries five entries now, and five
 * side-by-side would either wrap or crush "Move buildings…" to an ellipsis. */
/* A motion element: the site spreads the 'popover' overlay preset on it (grows
   from its top-right anchor) and, inside a Presence, it shrinks away on close. */
export const ActionModal = m.div`
  position: absolute;
  top: calc(100% + ${spacing.xs});
  right: ${spacing.xs};
  min-width: 208px;
  padding: ${spacing.xs};
  gap: ${spacing.half};
  border-radius: ${radius.md};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  box-shadow: ${shadow.md};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  z-index: ${zIndex.dropdown};
`;

export const ButtonContainer = styled.button<{ isDelete?: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  flex: 1;
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.sm};
  border: none;
  border-radius: ${radius.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};
  background: ${({ isDelete }) =>
    isDelete ? palette.alertSoft : palette.irisTintSoft};
  color: ${({ isDelete }) => (isDelete ? status.alert.fg : palette.lagoon)};

  &:hover:not(:disabled) {
    ${({ isDelete }) =>
      isDelete
        ? `background: ${palette.alertStrong}; color: ${palette.white};`
        : `background: ${palette.irisTint}; color: ${palette.irisDark};`}
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    background: ${palette.surfaceMuted};
    color: ${palette.slateLight};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ isDelete }) =>
      isDelete ? shadow.focusError : shadow.focus};
  }
`;

export const ButtonTitle = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  line-height: 1;
  margin: 0;
  color: inherit;
`;

/* ── Expanded buildings panel ──────────────────────────────────────────── */

export const ExpansionPanel = styled.div`
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  padding: ${spacing.sm};
  border-top: 1px solid ${palette.line};
  /* Matches the card's own radius so the panel sits flush inside it without
   * the parent needing overflow:hidden. */
  border-radius: 0 0 ${radius.md} ${radius.md};
  background: ${palette.surfaceMuted};

  ${up('md')} {
    padding: ${spacing.sm} ${spacing.md};
  }
`;

/* The panel's reveal: the site gives this the shared `collapse` variants, so
   the panel row unfolds to its height on expand and folds shut on collapse
   (it exits through the row's Presence before the row is removed). */
export const PanelReveal = m.div`
  overflow: hidden;
`;

export const PanelHeading = styled.p`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

/* The heading and the panel's own action share a line on tablet and up; the
 * heading's trailing margin is dropped here so the button centres against the
 * text rather than 4px above it. */
export const PanelHeaderRow = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.xs};
  margin-bottom: ${spacing.half};

  & > p {
    margin-bottom: 0;
  }

  ${up('md')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing.sm};
  }
`;

/* Secondary button — the panel's own actions ("Add buildings…", "Move…").
 * Quieter than NewPortfolioButton on purpose: these sit inside a row, not at
 * the top of the screen, and a second gradient CTA there would compete. */
export const PanelActionButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.half};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  white-space: nowrap;
  cursor: pointer;
  box-shadow: ${shadow.xs};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${up('lg')} {
    min-height: ${layout.controlHeight};
  }

  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
    color: ${palette.irisDark};
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    background: ${palette.surfaceMuted};
    border-color: ${palette.line};
    color: ${palette.slateLight};
    box-shadow: none;
    cursor: not-allowed;
  }
`;

export const BuildingItem = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing.half};
  padding: ${spacing.xs} ${spacing.sm};
  min-width: 0;
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lineStrong};
  }

  ${up('md')} {
    flex-direction: row;
    align-items: center;
    gap: ${spacing.sm};
    min-height: ${layout.touchTarget};
  }
`;

export const BuildingName = styled.p`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

export const BuildingMeta = styled.p`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};
`;

export const PanelNote = styled.p`
  margin: 0;
  padding: ${spacing.xs} 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  color: ${palette.slate};
`;

/**
 * A building row that can be picked. Unlike BuildingItem it stays a row at
 * every width — the checkbox has to keep its column on a phone, so the name and
 * the address stack inside BuildingTextStack instead of the row itself
 * collapsing.
 */
export const SelectableBuildingRow = styled(BuildingItem)<{
  isSelected?: boolean;
}>`
  flex-direction: row;
  align-items: center;
  gap: ${spacing.sm};
  min-height: ${layout.touchTarget};
  /* On a phone the trailing Pill and "Move…" button are both nowrap and
   * fixed-width; together with the checkbox and the gaps they eat ~260px of a
   * 360px viewport, leaving the building name a few characters wide. Letting
   * the row wrap gives the name its own full-width line above them. */
  flex-wrap: wrap;

  ${up('md')} {
    flex-direction: row;
    flex-wrap: nowrap;
  }

  ${({ isSelected }) =>
    isSelected &&
    css`
      background: ${palette.irisTint};
      border-color: ${palette.lagoon};

      &:hover {
        background: ${palette.irisTint};
        border-color: ${palette.lagoon};
      }
    `}
`;

/**
 * The building's cover photo in a portfolio row.
 *
 * A THUMBNAIL, NOT A PICTURE. 40px square, so it reads as an identifier next to
 * the name rather than as content — the row is a list entry, and a building
 * with a photo must not become twice as tall as one without.
 *
 * `flex: 0 0 auto` keeps it out of the wrap calculation: SelectableBuildingRow
 * wraps on a phone precisely because the name was being crushed, and a
 * thumbnail that could shrink would take that space back.
 */
export const BuildingThumb = styled.img`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: ${radius.xs};
  background: ${palette.surfaceMuted};
`;

/**
 * The same 40px box when there is no photo — drawn rather than omitted so the
 * names in a list stay on one left edge instead of stepping in and out
 * depending on which buildings happen to have pictures.
 */
export const BuildingThumbEmpty = styled.span`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: ${radius.xs};
  border: 1px dashed ${palette.line};
  background: ${palette.surfaceMuted};
`;

export const BuildingTextStack = styled.div`
  display: flex;
  /* Takes a whole line of its own on a phone (the row wraps around it), so the
   * name is never crushed between the checkbox and the two nowrap controls. */
  flex: 1 1 100%;
  min-width: 0;
  flex-direction: column;
  gap: ${spacing.half};

  ${up('md')} {
    flex: 1 1 auto;
    flex-direction: row;
    align-items: center;
    gap: ${spacing.sm};
  }
`;

export const BuildingCheckbox = styled.input`
  -webkit-appearance: none;
  appearance: none;
  -webkit-appearance: none;
  box-sizing: border-box;
  position: relative;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin: 0;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  cursor: pointer;
  transition: ${transition.colors};

  /* A 44px hit area on a 20px control, per design_system.md: grow the target
   * with padding, never with size. The pseudo-element takes no layout space, so
   * the row's spacing is unchanged and the tick still reads as a 20px box. */
  &::before {
    content: '';
    position: absolute;
    inset: -12px;
  }

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
  }

  &:checked {
    background: ${palette.iris};
    border-color: ${palette.lagoon};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    background: ${palette.surfaceMuted};
    border-color: ${palette.line};
    cursor: not-allowed;
  }
`;

/* THE TICK, AS A REAL ELEMENT. It used to be the input's own ::after with a
   local keyframe carrying the rotation through every frame; a pseudo-element
   cannot host a motion component, so the tick now sits in a slot over the
   checkbox and lands with the shared Pop. The tick is drawn from two borders
   rather than a background image: an inline SVG data URI would have to carry
   a literal hex, and colours only ever come from the palette. The rotation is
   on the inner mark and the pop's scale on the outer span, so neither fights
   the other. pointer-events: none — the input underneath is still the control. */
export const CheckboxSlot = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
`;

export const CheckTick = styled(Pop)`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

export const CheckTickMark = styled.span`
  position: absolute;
  top: 3px;
  left: 7px;
  width: 5px;
  height: 9px;
  border: solid ${palette.white};
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
`;

/* The selection footer. Tinted rather than white so it reads as a state the
 * panel is in, not as one more row in the list. */
export const BulkBar = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${spacing.xs};
  margin-top: ${spacing.half};
  padding: ${spacing.xs} ${spacing.sm};
  min-height: ${layout.touchTarget};
  background: ${palette.irisTint};
  border: 1px solid ${palette.iris};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.xs};
  animation: ${fadeInUp} ${duration.fast} ${easing.entrance} both;

  ${up('md')} {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${spacing.sm};
  }
`;

/* ── Feedback: error banner + skeletons ────────────────────────────────── */

export const ErrorBanner = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: ${spacing.xs};
  width: 100%;
  padding: ${spacing.sm};
  margin-bottom: ${spacing.xs};
  border: 1px solid ${palette.alert};
  border-radius: ${radius.sm};
  background: ${palette.alertSoft};
  color: ${status.alert.fg};

  & svg {
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin-top: ${spacing.half};
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${status.alert.fg};
  word-break: break-word;
`;

export const RetryButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  min-height: ${layout.buttonHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.alert};
  border-radius: ${radius.xs};
  background: transparent;
  color: ${status.alert.fg};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.alertStrong};
    color: ${palette.white};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focusError};
  }
`;

/* The skeleton rows this screen established now live in load-state.tsx as
   ListSkeleton, which every list screen shares; the local copies are gone. */

/* THE ERROR LINE, UNFOLDING. One component for every "{error && <ErrorBanner>}"
   site: InlineMessage animates the space open under the thing it explains and
   folds it away when the message clears. role="alert" is InlineMessage's, so
   the banner inside no longer carries its own. */
export const ErrorNotice: React.FC<{
  message: string | null | undefined;
  onRetry?: () => void;
  retryLabel?: string;
}> = ({ message, onRetry, retryLabel = 'Try again' }) => (
  <InlineMessage
    message={
      message ? (
        <ErrorBanner>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4.5" />
            <path d="M12 16h.01" />
          </svg>
          <ErrorText>{message}</ErrorText>
          {onRetry && (
            <RetryButton type="button" onClick={onRetry}>
              {retryLabel}
            </RetryButton>
          )}
        </ErrorBanner>
      ) : undefined
    }
  />
);

/* ── Empty state ───────────────────────────────────────────────────────── */

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: ${spacing.sm};
  padding: ${spacing.xxl} ${spacing.md};
  margin-top: ${spacing.xs};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};

  ${up('md')} {
    padding: ${spacing.xxxl} ${spacing.xl};
  }
`;

export const EmptyStateIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: ${radius.lg};
  background: ${palette.irisTint};
  color: ${palette.lagoon};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.xs};
  animation: ${scaleIn} ${duration.slow} ${easing.spring} both;

  & svg {
    width: 36px;
    height: 36px;
  }
`;

/* ── Modals (delete confirm + the add/edit form sheet) ─────────────────── */

/* Motion elements: scrimMotion / useOverlayMotion('modal') are spread on them
   at the site, and inside a Presence they animate OUT as well. */
export const ModalBackground = m.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: ${spacing.sm};
  background: ${palette.overlay};
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: ${zIndex.modal};

  ${down('sm')} {
    align-items: flex-end;
    padding: 0;
  }
`;

export const DeleteModal = m.div`
  box-sizing: border-box;
  background: ${palette.surface};
  width: 100%;
  max-width: 400px;
  padding: ${spacing.lg} ${spacing.md};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};

  ${down('sm')} {
    max-width: 100%;
    border-radius: ${radius.md} ${radius.md} 0 0;
    padding: ${spacing.lg} ${spacing.sm} ${spacing.xl};
  }
`;

export const DeleteIconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: ${radius.circle};
  background: ${palette.alertSoft};
  color: ${status.alert.fg};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing.sm};
  animation: ${scaleIn} ${duration.base} ${easing.spring} both;

  & svg {
    width: 28px;
    height: 28px;
  }
`;

export const CancelConfirmButton = styled.button<{ isConfirm?: boolean }>`
  box-sizing: border-box;
  display: flex;
  min-height: ${layout.touchTarget};
  width: 100%;
  justify-content: center;
  align-items: center;
  margin-bottom: ${spacing.xs};
  padding: 0 ${spacing.sm};
  align-self: stretch;
  border-radius: ${radius.sm};
  cursor: pointer;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.snug};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${({ isConfirm }) =>
    isConfirm
      ? css`
          background: ${palette.alertStrong};
          color: ${palette.white};
          border: none;
          box-shadow: ${shadow.sm};

          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: ${shadow.md};
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadow.focusError};
          }
        `
      : css`
          background: ${palette.surface};
          color: ${palette.slate};
          border: 1px solid ${palette.line};

          &:hover:not(:disabled) {
            background: ${palette.irisTintSoft};
            border-color: ${palette.lagoon};
            color: ${palette.lagoon};
          }
          &:focus-visible {
            outline: none;
            box-shadow: ${shadow.focus};
          }
        `}

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: progress;
  }
`;

/* ── Add / edit form sheet ─────────────────────────────────────────────── */

/* PortfolioFormCard, not FormModal: the old name collided with the shared
   components/modal/form-modal.tsx and the two were nothing alike. */
export const PortfolioFormCard = m.form`
  box-sizing: border-box;
  background: ${palette.surface};
  width: 100%;
  max-width: 480px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: ${radius.md};
  box-shadow: ${shadow.lg};
  overflow: hidden;

  ${down('sm')} {
    max-width: 100%;
    max-height: 92vh;
    max-height: 92dvh;
    border-radius: ${radius.md} ${radius.md} 0 0;
  }
`;

export const FormHeader = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  padding: ${spacing.md};
  border-bottom: 1px solid ${palette.line};
`;

export const FormBody = styled.div`
  box-sizing: border-box;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing.xs} ${spacing.md} ${spacing.md};
`;

export const InputContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  margin-top: ${spacing.sm};
  gap: ${spacing.xs};
`;

export const Label = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.medium};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.inkSoft};
  cursor: pointer;
`;

export const HelperText = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.slateLight};
`;

const fieldBase = css`
  box-sizing: border-box;
  width: 100%;
  padding: 0 ${spacing.sm};
  border-radius: ${radius.xs};
  border: 1px solid ${palette.line};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  box-shadow: ${shadow.xs};
  transition: ${transition.colors};

  ::placeholder {
    color: ${palette.slateLight};
  }

  &:hover {
    border-color: ${palette.lineStrong};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

export const StyledPortfolioInput = styled.input<{ isInvalid?: boolean }>`
  ${fieldBase}
  height: ${layout.fieldHeight};

  ${({ isInvalid }) =>
    isInvalid &&
    css`
      border-color: ${palette.alert};

      &:focus,
      &:focus-visible {
        border-color: ${palette.alert};
        box-shadow: ${shadow.focusError};
      }
    `}
`;

export const StyledPortfolioTextarea = styled.textarea`
  ${fieldBase}
  min-height: 96px;
  padding: ${spacing.sm};
  resize: vertical;
  line-height: ${typography.leading.normal};
`;

/* The native disclosure arrow is kept: replacing it means a background-image
 * data URI with a hardcoded hex in it, which the palette rule forbids, and the
 * platform control is the one every user already recognises. */
export const StyledPortfolioSelect = styled.select`
  ${fieldBase}
  height: ${layout.fieldHeight};
  cursor: pointer;

  &:disabled {
    background: ${palette.surfaceMuted};
    color: ${palette.slateLight};
    cursor: not-allowed;
  }
`;

export const ErrorLabel = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  color: ${status.alert.fg};
  display: block;
`;

export const ButtonsContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.sm} ${spacing.md};
  border-top: 1px solid ${palette.line};
  background: ${palette.surface};

  ${down('sm')} {
    padding: ${spacing.sm};

    & > button {
      flex: 1;
    }
  }
`;

export const CancelButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.slate};
  cursor: pointer;
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${up('lg')} {
    height: ${layout.controlHeight};
  }

  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
    color: ${palette.lagoon};
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const SaveButton = styled.button`
  -webkit-appearance: none;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  height: ${layout.touchTarget};
  padding: 0 ${spacing.md};
  border: none;
  border-radius: ${radius.sm};
  background: ${gradient.primary};
  color: ${palette.white};
  cursor: pointer;
  box-shadow: ${shadow.brand};
  transition: ${transition.colors},
    transform ${duration.fast} ${easing.standard};

  ${up('lg')} {
    height: ${layout.controlHeight};
  }

  & svg {
    transition: transform ${duration.fast} ${easing.standard};
  }

  &:hover:not(:disabled) {
    background: ${gradient.primaryHover};
    box-shadow: ${shadow.brandHover};
    transform: translateY(-1px);
  }

  &:hover:not(:disabled) svg {
    transform: translateX(2px);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.brand}, ${shadow.focus};
  }

  &:disabled {
    background: ${palette.lineStrong};
    color: ${palette.slateLight};
    box-shadow: none;
    cursor: progress;
  }
`;
